import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { organizerService, searchService } from "../services/api";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { ROLES } from "../config/roles";
import { usePermission } from "../hooks/usePermission";
import { useMediaQuery } from "../hooks/useMediaQuery";

const Organizers = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // QAF01-056: the directory endpoint is platform-staff-only by design
  // (users/permissions.py:499-514, nailed down by
  // test_qav5_213_organizer_directory_gate.py). Its 403 is an expected gate, not
  // an incident — kept apart from `error` so the page can explain instead of
  // painting the red failure state.
  const [restricted, setRestricted] = useState(false);
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const { currentRole } = useSelector((state) => state.auth);
  const hasTalkPermission = usePermission("talk_to_organizer");
  const requiresPermission =
    currentRole === ROLES.Staff || 
    currentRole === ROLES.ClubAdmin || 
    currentRole === ROLES.Coach;
  const canContactOrganizer = requiresPermission ? hasTalkPermission : true;
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Fetch countries on component mount
  useEffect(() => {
    fetchCountries();
  }, []);

  // Fetch organizers on component mount and when filters change
  useEffect(() => {
    fetchOrganizers();
  }, [searchQuery, selectedCountry]);

  const fetchCountries = async () => {
    try {
      const response = await searchService.getCountries();
      if (response.success) {
        setCountries(response.data);
        setFilteredCountries(response.data);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchOrganizers = async () => {
    setLoading(true);
    setError(null);
    setRestricted(false);

    try {
      const params = {
        search: searchQuery || undefined,
        country: selectedCountry || undefined,
      };

      const response = await organizerService.getOrganizers(params);

      if (response.success) {
        setOrganizers(response.data);
      } else if (response.status === 403 || response.status === 401) {
        // Expected access gate — show the explanation, never the red error
        // state (same treatment as MessageRequestsPanel.js:238-243).
        setOrganizers([]);
        setRestricted(true);
      } else {
        setError(response.error);
        toast.error(response.error);
      }
    } catch (error) {
      console.error("Error fetching organizers:", error);
      setError("Failed to fetch organizers");
      toast.error("Failed to fetch organizers");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchOrganizers();
  };

  const handleCountrySearch = (searchTerm) => {
    setCountrySearch(searchTerm);
    if (searchTerm.trim()) {
      const filtered = countries.filter((country) =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country.name);
    setCountrySearch(country.name);
    setIsCountryDropdownOpen(false);
  };

  const handleClearCountry = () => {
    setSelectedCountry("");
    setCountrySearch("");
  };

  const handleContactOrganizer = async (organizer) => {
    if (!canContactOrganizer) {
      return;
    }
    try {
      const response = await organizerService.initiateOrganizerChat(
        organizer.account_id
      );

      if (response.success) {
        toast.success("Chat initiated successfully!");
        // Navigate to inbox with the channel information
        navigate("/inbox", {
          state: {
            channelId: response.channelId,
            channelType: response.channelType,
          },
        });
      } else {
        toast.error(response.error || "Failed to initiate chat");
      }
    } catch (error) {
      console.error("Error initiating chat:", error);
      toast.error("Failed to initiate chat. Please try again.");
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className=" mx-auto pb-6">
      {isMobile ? (
        <>
          <div className="px-4 py-4 border-y border-[#0000001A] text-base font-bold">
            Organizers
          </div>

          <div className="px-4 py-4 border-b border-[#0000001A] flex items-center justify-between gap-2">
            <div className="relative w-96">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="#2C2C2C"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search organizer"
                className="block w-full pl-10 pr-3 py-2 border border-[#D2D4D8] rounded-lg text-sm placeholder:text-[#1E1E1EB2] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            {/* Country Filter Dropdown */}
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  className="w-28 px-3 py-2 border border-[#D2D4D8] rounded-lg text-sm placeholder:text-[#1E1E1ECC] text-[#1E1E1ECC] focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Country"
                  value={countrySearch}
                  onChange={(e) => {
                    handleCountrySearch(e.target.value);
                    setIsCountryDropdownOpen(true);
                  }}
                  onClick={() => setIsCountryDropdownOpen(true)}
                />
                {selectedCountry && (
                  <button
                    type="button"
                    className="absolute right-8 top-1/2 -translate-y-1/2 p-1 hover:text-red-500"
                    onClick={handleClearCountry}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="#1E1E1ECC"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <button
                    type="button"
                    className="p-1"
                    onClick={() =>
                      setIsCountryDropdownOpen(!isCountryDropdownOpen)
                    }
                  >
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="#1E1E1ECC"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {isCountryDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country) => (
                      <button
                        key={country.id}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => handleCountrySelect(country)}
                      >
                        {country.image_url && (
                          <img
                            src={country.image_url}
                            alt={country.name}
                            className="w-5 h-4 object-cover"
                          />
                        )}
                        <span>{country.name}</span>
                        {country.fifa_name && (
                          <span className="text-xs text-gray-500">
                            ({country.fifa_name})
                          </span>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No countries found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex px-12 py-4 items-center justify-between border border-[#E5E6E8]">
          <div>
            <h1 className="text-xl font-bold text-[#202630]">Organizers</h1>
          </div>
          <div className="flex justify-between items-center gap-4">
            <div className="relative w-96">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="#2C2C2C"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search organizer"
                className="block w-full pl-10 pr-3 py-2 border border-[#D2D4D8] rounded-lg text-sm placeholder:text-[#1E1E1EB2] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>

            {/* Country Filter Dropdown */}
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  className="w-40 px-3 py-2 border border-[#D2D4D8] rounded-lg text-sm placeholder:text-[#1E1E1ECC] text-[#1E1E1ECC] focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Country"
                  value={countrySearch}
                  onChange={(e) => {
                    handleCountrySearch(e.target.value);
                    setIsCountryDropdownOpen(true);
                  }}
                  onClick={() => setIsCountryDropdownOpen(true)}
                />
                {selectedCountry && (
                  <button
                    type="button"
                    className="absolute right-8 top-1/2 -translate-y-1/2 p-1 hover:text-red-500"
                    onClick={handleClearCountry}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="#1E1E1ECC"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <button
                    type="button"
                    className="p-1"
                    onClick={() =>
                      setIsCountryDropdownOpen(!isCountryDropdownOpen)
                    }
                  >
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="#1E1E1ECC"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {isCountryDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country) => (
                      <button
                        key={country.id}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        onClick={() => handleCountrySelect(country)}
                      >
                        {country.image_path && (
                          <img
                            src={country.image_path}
                            alt={country.name}
                            className="w-5 h-4 object-cover"
                          />
                        )}
                        <span>{country.name}</span>
                        {country.fifa_name && (
                          <span className="text-xs text-gray-500">
                            ({country.fifa_name})
                          </span>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No countries found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Organizers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 px-4 md:px-12 py-6 bg-[#F8F9FB]">
        {loading ? (
          // Loading state
          Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-[#D2D4D880] overflow-hidden animate-pulse"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-gray-200"></div>
                  <div className="h-5 bg-gray-200 rounded w-24"></div>
                </div>
                <div className="bg-[#F7F7F7] rounded-lg p-3 space-y-2 mb-4">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </div>
                <div className="w-full h-10 bg-gray-200 rounded-lg mt-6"></div>
              </div>
            </div>
          ))
        ) : restricted ? (
          // Access-gate state (QAF01-056). Same neutral styling as the empty
          // state below, so nothing new enters the design system.
          <div className="col-span-4 text-center py-12">
            <p className="text-gray-500">
              The organizer directory is open to TransferPitch staff only.
            </p>
            <p className="text-gray-500 text-sm mt-1">
              Your account does not include access to this list.
            </p>
          </div>
        ) : error ? (
          // Error state
          <div className="col-span-4 text-center py-12">
            <p className="text-red-500">{error}</p>
            <button
              onClick={() => fetchOrganizers()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Try Again
            </button>
          </div>
        ) : organizers.length === 0 ? (
          // Empty state
          <div className="col-span-4 text-center py-12">
            <p className="text-gray-500">No organizers found</p>
          </div>
        ) : (
          // Organizers list
          organizers.map((organizer) => (
            <div
              key={organizer.account_id}
              className="bg-white rounded-xl border border-[#D2D4D880] overflow-hidden"
            >
              <div className="p-6">
                {/* Header with Image/Badge */}
                <div className="flex items-center gap-3 mb-4">
                  {organizer.profile_picture_url ? (
                    <img
                      src={organizer.profile_picture_url}
                      alt={organizer.name}
                      className="w-9 h-9 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                      {getInitials(organizer.name)}
                    </div>
                  )}
                  <h3 className="font-medium text-[18px] text-[#242426]">
                    {organizer.name}
                  </h3>
                </div>

                {/* Contact Info */}
                <div className="bg-[#F7F7F7] rounded-lg p-3 space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg
                      width="13"
                      height="12"
                      viewBox="0 0 13 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.78742 2.5C8.27579 2.59528 8.72461 2.83413 9.07645 3.18597C9.42829 3.53781 9.66714 3.98663 9.76242 4.475M7.78742 0.5C8.80206 0.612718 9.74821 1.06709 10.4705 1.7885C11.1929 2.50992 11.6484 3.4555 11.7624 4.47M11.2624 8.46V9.96C11.263 10.0993 11.2345 10.2371 11.1787 10.3647C11.1229 10.4923 11.0411 10.6068 10.9385 10.7009C10.8358 10.7951 10.7147 10.8667 10.5828 10.9114C10.4509 10.956 10.3111 10.9725 10.1724 10.96C8.63383 10.7928 7.15592 10.2671 5.85742 9.425C4.64933 8.65733 3.62509 7.63309 2.85742 6.425C2.01241 5.1206 1.48654 3.6355 1.32242 2.09C1.30992 1.95173 1.32636 1.81238 1.37067 1.68081C1.41498 1.54924 1.4862 1.42834 1.5798 1.32581C1.6734 1.22328 1.78732 1.14135 1.91431 1.08526C2.04131 1.02917 2.17859 1.00013 2.31742 1H3.81742C4.06007 0.997612 4.29531 1.08354 4.4793 1.24177C4.66328 1.39999 4.78346 1.61972 4.81742 1.86C4.88073 2.34003 4.99814 2.81136 5.16742 3.265C5.23469 3.44396 5.24925 3.63846 5.20937 3.82544C5.16949 4.01242 5.07685 4.18406 4.94242 4.32L4.30742 4.955C5.0192 6.20677 6.05565 7.24322 7.30742 7.955L7.94242 7.32C8.07836 7.18557 8.24999 7.09292 8.43698 7.05305C8.62396 7.01317 8.81846 7.02773 8.99742 7.095C9.45105 7.26428 9.92239 7.38169 10.4024 7.445C10.6453 7.47926 10.8671 7.6016 11.0257 7.78875C11.1843 7.97589 11.2685 8.21479 11.2624 8.46Z"
                        stroke="#1E1E1E"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>

                    <p className="text-xs text-[#222A35]">
                      {" "}
                      {organizer.mobile}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg
                      width="12"
                      height="10"
                      viewBox="0 0 12 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.2617 2C11.2617 1.45 10.8117 1 10.2617 1H2.26172C1.71172 1 1.26172 1.45 1.26172 2M11.2617 2V8C11.2617 8.55 10.8117 9 10.2617 9H2.26172C1.71172 9 1.26172 8.55 1.26172 8V2M11.2617 2L6.26172 5.5L1.26172 2"
                        stroke="#1E1E1E"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>

                    <p className="text-xs text-[#222A35]">{organizer.email}</p>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#242426B2]">
                      Organizer type
                    </span>
                    <span className="text-sm text-[#242426]">
                      {organizer.organizer_type}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#242426B2]">
                      Organize matches
                    </span>
                    <span className="text-sm text-[#242426]">
                      {organizer.can_organize_matches ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#242426B2]">
                      Confederation
                    </span>
                    <span className="text-sm text-[#242426]">
                      {organizer.confideration}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#242426B2]">Country</span>
                    <div className="flex items-center gap-2">
                      {organizer.country && (
                        <img
                          src={`https://flagcdn.com/${organizer.country.toLowerCase()}.svg`}
                          alt={organizer.country}
                          className="w-5 h-3.5"
                        />
                      )}
                      <span className="text-sm text-[#242426]">
                        {organizer.country}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Button */}
                <button
                  onClick={() => handleContactOrganizer(organizer)}
                  disabled={!canContactOrganizer}
                  aria-disabled={!canContactOrganizer}
                  title={
                    !canContactOrganizer
                      ? "You don’t have permission to contact organizers"
                      : undefined
                  }
                  className={`w-full mt-6 py-2.5 text-center text-sm font-medium border rounded-lg transition-colors ${
                    canContactOrganizer
                      ? "text-[#1E1E1E] border-[#E6E6E6] hover:bg-gray-50"
                      : "text-[#9CA3AF] border-[#E6E6E6] opacity-60 cursor-not-allowed"
                  }`}
                >
                  Contact Organizer
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Organizers;
